import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { UserAuthApiService } from '../../shared/services/auth/user-auth-api.service';

/**
 * Manages user inactivity tracking and session expiration.
 * - Warns user at 55 minutes of inactivity
 * - Shows 5-minute countdown warning
 * - Provides "Stay logged in" button to reset timer
 * - Auto-logs out at 60 minutes of total inactivity
 * 
 * Activity is tracked by:
 * - Mouse/keyboard events (detected in AppComponent)
 * - API responses (detected in auth interceptor)
 * 
 * Uses activity-based keep-alive queueing:
 * - Queues keep-alive after 4 minutes of activity
 * - Deduplicates multiple activity events within the queue window
 * - Fires keep-alive to extend backend cookie (sliding expiration)
 */
@Injectable({ providedIn: 'root' })
export class SessionInactivityService {
    private readonly userAuthApiService = inject(UserAuthApiService);
    private readonly router = inject(Router);
    private readonly destroyRef = inject(DestroyRef);

    // Timings (in seconds)
    private readonly INACTIVITY_WARNING_TIME = 55 * 60; // 55 minutes
    private readonly LOGOUT_TIME = 60 * 60; // 60 minutes total
    private readonly WARNING_DURATION = 5 * 60; // 5 minute countdown from warning to logout
    private readonly KEEP_ALIVE_QUEUE_DELAY = 4 * 60; // Queue keep-alive after 4 minutes of activity
    private readonly TIMER_TICK = 1; // Check every 1 second

    // State signals
    private readonly secondsOfInactivity = signal<number>(0);
    private readonly hasQueuedKeepAlive = signal<boolean>(false);
    private isTracking = false;

    // Timer references for cleanup
    private inactivityTimerId: number | null = null;
    private keepAliveTimerId: number | null = null;

    // Derived computed signals (exported for template use)
    readonly isWarningActive = computed(() => {
        const inactivity = this.secondsOfInactivity();
        return inactivity >= this.INACTIVITY_WARNING_TIME && inactivity < this.LOGOUT_TIME;
    });

    readonly countdownSeconds = computed(() => {
        const inactivity = this.secondsOfInactivity();
        if (inactivity < this.INACTIVITY_WARNING_TIME) {
            return 0;
        }
        const elapsed = inactivity - this.INACTIVITY_WARNING_TIME;
        const remaining = this.WARNING_DURATION - elapsed;
        return Math.max(0, remaining);
    });

    readonly countdownDisplay = computed(() => {
        const seconds = this.countdownSeconds();
        const minutes = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${minutes}:${secs.toString().padStart(2, '0')}`;
    });

    constructor() {
        // Cleanup on service destroy
        this.destroyRef.onDestroy(() => {
            this.cleanup();
        });
    }

    /**
     * Start tracking user inactivity. Called from AppComponent.
     */
    startTracking(): void {
        if (this.isTracking) {
            return; // Already tracking
        }

        this.isTracking = true;
        this.secondsOfInactivity.set(0);
        this.hasQueuedKeepAlive.set(false);
        this.startInactivityTimer();
    }

    /**
     * Called when user activity is detected (mouse/keyboard/API response).
     * Resets inactivity counter and manages keep-alive queue.
     */
    resetActivity(): void {
        if (!this.isTracking) {
            return;
        }

        this.secondsOfInactivity.set(0);

        // The snackbar will close naturally as isWarningActive becomes false
        // via the computed signal reactivity.

        // Queue keep-alive if not already queued
        if (!this.hasQueuedKeepAlive()) {
            this.queueKeepAlive();
        }
    }

    /**
     * Logout user immediately.
     */
    logout(): void {
        this.cleanup();
        this.userAuthApiService
            .logout()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                complete: () => {
                    this.router.navigate(['/login']);
                },
                error: () => {
                    // Even if logout fails, redirect to login
                    this.router.navigate(['/login']);
                }
            });
    }

    /**
     * Private: Start the main inactivity timer that increments every second.
     */
    private startInactivityTimer(): void {
        this.inactivityTimerId = window.setInterval(() => {
            const current = this.secondsOfInactivity();
            this.secondsOfInactivity.set(current + this.TIMER_TICK);

            // Check if we've reached logout time
            if (current + this.TIMER_TICK >= this.LOGOUT_TIME) {
                this.logout();
            }
        }, this.TIMER_TICK * 1000);
    }

    /**
     * Private: Queue a keep-alive call to fire after 4 minutes of activity.
     * During those 4 minutes, any further activity will not queue another keep-alive (dedup).
     */
    private queueKeepAlive(): void {
        this.hasQueuedKeepAlive.set(true);

        this.keepAliveTimerId = window.setTimeout(() => {
            this.fireKeepAlive();

            // After firing, check if activity occurred during the queue period
            // If inactivity is 0 or low, activity happened, so queue another
            if (this.secondsOfInactivity() < this.KEEP_ALIVE_QUEUE_DELAY) {
                this.queueKeepAlive();
            } else {
                // No activity during queue period, clear the queue
                this.hasQueuedKeepAlive.set(false);
            }
        }, this.KEEP_ALIVE_QUEUE_DELAY * 1000);
    }

    /**
     * Private: Fire the keep-alive API call to extend backend session (sliding expiration).
     */
    private fireKeepAlive(): void {
        this.userAuthApiService
            .keepAlive()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                error: (err) => {
                    // Log error but don't stop inactivity tracking
                    console.error('Keep-alive failed:', err);
                }
            });
    }

    /**
     * Private: Clean up all timers and state.
     */
    private cleanup(): void {
        if (this.inactivityTimerId !== null) {
            clearInterval(this.inactivityTimerId);
            this.inactivityTimerId = null;
        }

        if (this.keepAliveTimerId !== null) {
            clearTimeout(this.keepAliveTimerId);
            this.keepAliveTimerId = null;
        }

        this.isTracking = false;
    }
}
